import { type LinearConfiguratorState } from "./types";
import {
  CUSTOM_SEGMENT_LENGTH_SELECTED,
  CUSTOM_SEGMENT_LENGTH_SELECTED_RESET,
  HIDE_LOADER,
  LOAD_DATA,
  MARK_ALREADY_ADDED_TO_CART,
  POST_HYBRIS_UPDATE,
  RESET_CURRENT_STEP,
  RESTORE_SNAPSHOT,
  SET_STEP,
  SET_TAB,
  type setLCSummaryTabAction,
  SHOW_LOADER,
  TOGGLE_LINEAR_CARRIAGE_EDIT,
  UPDATE_ADDITIONAL_DATA,
  UPDATE_CSTICS,
  UPDATE_CSTICS_FROM_TILE,
  UPDATE_PRICE_REFERENCE,
  UPDATE_QUANTITY,
  UPDATE_SMDS_FLIP,
  VALIDATE_CURRENT_STEP,
} from "./actions";
import { type AnyAction } from "../../../redux/types";
import {
  type ConflictKmat,
  type Kmat,
  type LinearConfiguration,
  type LinearConfigurationUpdate,
  type LinearSubstep,
  SimpleCsticId,
  type SubstepTile,
} from "../models";
import { SubstepType } from "../dtos";
import {
  csticUpdatesFromDict,
  findSelectedTileIndex,
  fromExtraValidationToDirtyParamName,
  getCsticNumberVal,
  getCsticVal,
} from "../utils";
import { type CsticUpdate } from "../api/sendLinearConfiguratorUpdate";
import {
  CSTIC_AL_NAME,
  CSTIC_AR_NAME,
  CSTIC_LENGTH_NAME,
  CSTIC_PART_MAX_SIZE_NAME,
  CSTIC_PART_MIN_SIZE_NAME,
  CSTIC_SCHMIERANSCHLUSS_NAME,
  CSTIC_SCHMIERANSCHLUSS_POSITION_NAME,
  FAKE_CSTIC_UNIT_LENGTH,
  GET_GUIDEWAY_PIECE_SIZE_CSTIC_NAME,
  getEditCarriageCsticsToReset,
  isGuidewayPieceSizeCsticName,
  UNMOUNTED_LEFT,
  UNMOUNTED_RIGHT,
} from "../constants";
import { ConfiguratorType } from "../../ConfiguratorType";

import { isFakeNoClosingPLugsTileSelected } from "../components/Substeps/ClosureSubstep";
import { type Draft, produce } from "immer";
import {
  EXTRA_DATA_DIRTY_PARAM_PREFIX,
  getDirtyParamName,
  getExtraValidationParamName,
} from "~/Configurators/Linear/namingUtils";
import { LcSummaryTab } from "~/Configurators/common/LcSummaryTab";
import { selectCsticExpandedFromNested } from "~/Configurators/Linear/redux/selectCstic";
import * as MetricCstics from "~/Configurators/Shaft/metric/metricCsticConstants";
import { axialThread } from "~/Configurators/Shaft/metric/metricCsticConstants";
import * as ImperialCstics from "~/Configurators/Shaft/inch/inchCsticConstants";
import { KmatType } from "~/Configurators/Linear/getKmatType";
import {
  type ShaftMachiningExtraError,
  showAxialThreadSection,
  showChamferedSection,
  showRadialThreadSection,
  validateAxialThread,
  validateChamferSegment,
  validateRadialThreadMetric,
} from "~/Configurators/Shaft/shaftConfiguratorHelper";
import { getMainKmat } from "~/Configurators/Shaft/common/commonShaftHelpers";
import {
  CARRIAGE_CONFIGURATOR_PRODUCT_CODE,
  RAIL_CONFIGURATOR_PRODUCT_CODE,
  TKDM_RAIL_CONFIGURATOR_PRODUCT_CODE,
  TKLT_RAIL_CONFIGURATOR_PRODUCT_CODE,
} from "~/Configurators/constants";
import { AlarHardMax } from "~/Configurators/Rail/v2/configuration/classic/RailsCstics";
import { csticHasValue } from "~/Configurators/common/v2/helper";

const EMPTY_SUBSTEP: LinearSubstep = {
  groupedTiles: [],
  tiles: [],
  inputCstics: {},
  helperCstics: {},
  selectedTile: null,
  additionalValidations: {},
  additionalData: {},
  description: "",
  info: "",
  name: "",
  tilesPerRow: 0,
  type: SubstepType.SummaryPage,
  value: null,
  footerInfo: [],
  extraTexts: {},
};

export const initialState: LinearConfiguratorState = {
  currentStep: 0, //change after creating summary
  highTideStep: 0,
  activeTab: LcSummaryTab.ProductInformation,
  kmats: {},
  steps: [],
  substeps: {},
  systemLayoutHelpTile: null,

  groupIdToDisplay: "",
  productCode: "",
  configId: "",

  minLength: null,
  maxLength: null,
  allowedSteps: [[], []],
  showLoader: false,

  conflicts: [],

  designation: "",
  schaefflerId: "",
  description: "",

  guidewayPieceLimits: {},
  quantity: 1,
  isConfigComplete: false,
  manufacturerLogo: null,
  step5PdfLink: null,

  helpUrl: "",
  productImageUrl: "",
  specialTranslations: {},
  advancedSpecialTranslations: {},
  resetCarriageDetails: true,
  enableConfigureCarriageValues: true,
  customSegmentLengthSelected: false,
  customSecondSegmentLengthSelected: false,
  customSectionHeightSelected: false,
  defaultPlugsApplied: false,
  cadUrl: "",
  cadXmlRequest: "",
  addedToCart: false,
  priceReference: "",
  enableCarriageEdit: [],
  kbInfo: [],
};

function shouldUseNewValidation(draft: Draft<LinearConfiguratorState>) {
  return (
    draft.productCode === TKDM_RAIL_CONFIGURATOR_PRODUCT_CODE ||
    draft.productCode === TKLT_RAIL_CONFIGURATOR_PRODUCT_CODE ||
    draft.productCode === CARRIAGE_CONFIGURATOR_PRODUCT_CODE
  );
}

function validateFromDefinition(substep: LinearSubstep, draft: Draft<LinearConfiguratorState>) {
  const mainKmat = getMainKmat({ kmats: draft.kmats });
  const conflictedCstics = getConflictCsticNames(draft.conflicts);

  let valid = true;
  const extraValidations: Record<string, boolean> = {};
  const additionalData: Record<string, boolean> = {};

  if (!substep.isVisible) {
    return {
      substepValid: true,
      extraValidations: {},
      additionalData: {},
    };
  }

  if (substep.tiles.length || substep.groupedTiles.length) {
    // Validate that a tile is selected
    valid = substep.selectedTile !== null;
  }
  Object.values(substep.inputCstics).forEach((cstic) => {
    let csticValid = true;

    if (cstic.visible) {
      csticValid = !conflictedCstics.includes(cstic.name);
    }

    if (cstic.visible && cstic.required) {
      csticValid = csticValid && csticHasValue(mainKmat, cstic.name);
    }
    valid = valid && csticValid;
    const key = getExtraValidationParamName(SimpleCsticId(cstic.name));
    extraValidations[key] = csticValid;
    const dirtyKey = fromExtraValidationToDirtyParamName(key);
    additionalData[dirtyKey] = true;
  });

  return {
    substepValid: valid,
    extraValidations,
    additionalData,
  };
}

export const linearConfiguratorReducer = (
  state: LinearConfiguratorState = initialState,
  action: AnyAction,
): LinearConfiguratorState => {
  function validateStep(draft: Draft<LinearConfiguratorState>, substepType: SubstepType) {
    const substep = draft.substeps[substepType];

    if (shouldUseNewValidation(draft)) {
      const { substepValid, extraValidations, additionalData } = validateFromDefinition(substep, draft);
      substep.isInvalid = !substepValid;
      substep.isLenientInvalid = !substepValid;
      Object.entries(extraValidations).forEach(([key, value]) => {
        substep.additionalValidations[key] = value;
      });
      Object.entries(additionalData).forEach(([key, value]) => {
        substep.additionalData[key] = value;
      });
    } else {
      const validator = VALIDATORS[substepType] || DEFAULT_VALIDATION;
      const validationResult = validator(draft, substep);
      const isInvalid = !validationResult.substepValid;
      const validationResultDirtyKeys = Object.keys(validationResult.extraValidations).map(
        fromExtraValidationToDirtyParamName,
      );
      substep.isInvalid = isInvalid;
      substep.isLenientInvalid = isInvalid;
      validationResultDirtyKeys.forEach((key) => {
        substep.additionalData[key] = true;
      });
      Object.entries(validationResult.extraValidations).forEach(([key, value]) => {
        substep.additionalValidations[key] = value;
      });
    }
  }

  switch (action.type) {
    case SHOW_LOADER: {
      return {
        ...state,
        showLoader: true,
      };
    }
    case HIDE_LOADER: {
      return {
        ...state,
        showLoader: false,
      };
    }

    case LOAD_DATA: {
      const { data, helpUrl, type } = action.payload as {
        data: LinearConfiguration;
        helpUrl: string;
        type: ConfiguratorType;
      };
      const { substeps, kmats: kmatMap, steps, ...rest } = data;

      const currentStep = rest.currentStep - 1;
      const highestVisitedStep = rest.highestVisitedStep - 1;

      Object.values(substeps).forEach((substep) => {
        substep.selectedTile = findSelectedTileIndex(substep, kmatMap);
      });

      steps.forEach((step, index) => (step.active = rest.allowedSteps[highestVisitedStep][index]));

      const configureRailSubstep = substeps[SubstepType.ConfigureRail];
      if (configureRailSubstep) {
        (kmatMap[KmatType.GUIDEWAY] ?? []).forEach((kmat, index) => {
          if (kmat.cstics[CSTIC_LENGTH_NAME].value) {
            const key = getExtraValidationParamName({
              key: CSTIC_LENGTH_NAME,
              index,
              type: KmatType.GUIDEWAY,
            });
            // Is dirty = true
            configureRailSubstep.additionalData[key] = true;
          }
        });
      }

      const newState = {
        ...state,
        ...rest,
        substeps,
        steps,
        kmats: kmatMap,
        currentStep: currentStep,
        highTideStep: highestVisitedStep,
        helpUrl,
        type,
        productImageUrl: data.mediaUrl,
      };
      activateRailsSummaryAfterStep4LengthFilled(newState);
      return {
        ...newState,
        previousState: { ...newState, previousState: undefined },
      };
    }

    case POST_HYBRIS_UPDATE: {
      const {
        hybrisData: {
          kmats: kmatMap,
          steps: updatedSteps,
          substeps: updatedSubsteps,
          conflictedCstics,
          minLength: minLengthValue,
          maxLength: maxLengthValue,
          mediaUrl,
          currentStep: currentStepValue,
          highestVisitedStep: highestVisitedStepValue,
          ...rest
        },
        navigationUpdate,
      } = action.payload as {
        hybrisData: LinearConfigurationUpdate;
        navigationUpdate?: boolean;
      };

      const newState = produce(state, (draft) => {
        draft.steps.forEach((originalStep, index) => {
          const stepUpdate = updatedSteps[index];
          if (stepUpdate?.defaultValues) {
            originalStep.defaultValues = {
              ...originalStep.defaultValues,
              ...stepUpdate.defaultValues,
            };
          }
        });

        draft.highTideStep = navigationUpdate ? state.highTideStep : state.currentStep;
        if (rest.allowedSteps) {
          draft.allowedSteps = rest.allowedSteps;
        }
        if (rest.nextSteps) {
          draft.nextSteps = rest.nextSteps;
        }
        if (rest.previousSteps) {
          draft.previousSteps = rest.previousSteps;
        }
        if (!navigationUpdate) {
          draft.steps.forEach((step, index) => (step.active = draft.allowedSteps![draft.highTideStep][index]));
        }

        // Updated substeps only contains dynamic substeps, in that case we trust BE to provide available tiles each time
        Object.values(updatedSubsteps).forEach((newSubstep) => {
          const oldSubstep = draft.substeps[newSubstep.type];
          const updatedSubstep: LinearSubstep = {
            ...(oldSubstep || EMPTY_SUBSTEP),
            ...newSubstep,
            additionalData: {
              ...(oldSubstep || EMPTY_SUBSTEP).additionalData,
              ...newSubstep.additionalData,
            },
          };
          draft.substeps[updatedSubstep.type] = updatedSubstep;
        });

        Object.values(draft.substeps).forEach((substep) => {
          substep.selectedTile = findSelectedTileIndex(substep, kmatMap);
        });

        const substeps = draft.substeps;

        draft.kmats = kmatMap;
        draft.conflicts = conflictedCstics;
        draft.minLength = minLengthValue;
        draft.maxLength = maxLengthValue;
        draft.showLoader = false;
        draft.previousState = conflictedCstics.length > 0 ? draft.previousState : undefined;
        draft.productImageUrl = mediaUrl;

        Object.assign(draft, rest);

        if (substeps[SubstepType.GuidewaySet]) {
          const extraParams = updateGuidewaySetValidations(draft, true);
          const substep = draft.substeps[SubstepType.GuidewaySet];
          Object.assign(substep.additionalValidations, extraParams);

          const isInvalid = Object.values(substep.additionalValidations).some((valid) => !valid);
          const isLenientInvalid = Object.entries(substep.additionalValidations).some(([key, valid]) => {
            if (isGuidewayPieceSizeCsticName(key) || key.includes(CSTIC_LENGTH_NAME)) {
              const isDirty = substep.additionalData[fromExtraValidationToDirtyParamName(key)] ?? false;
              return !valid && isDirty;
            }
            return !valid;
          });
          substep.isInvalid = isInvalid;
          substep.isLenientInvalid = isLenientInvalid;
        }

        if (substeps[SubstepType.ConfigureRail]) {
          const extraParams = updateGuidewaySetValidations(draft, true);
          const substep = draft.substeps[SubstepType.ConfigureRail];
          Object.assign(substep.additionalValidations, extraParams);

          const isInvalid = Object.values(substep.additionalValidations).some((valid) => !valid);
          const isLenientInvalid = Object.entries(substep.additionalValidations).some(([key, valid]) => {
            if (isGuidewayPieceSizeCsticName(key) || key.includes(CSTIC_LENGTH_NAME)) {
              const isDirty = substep.additionalData[fromExtraValidationToDirtyParamName(key)] ?? false;
              return !valid && isDirty;
            }
            return !valid;
          });
          substep.isInvalid = isInvalid;
          substep.isLenientInvalid = isLenientInvalid;
        }

        if (substeps[SubstepType.CarriageDetails]) {
          const substep = substeps[SubstepType.CarriageDetails];
          const validationResult = VALIDATORS[SubstepType.CarriageDetails](draft, substep);
          Object.assign(substep.additionalValidations, validationResult.extraValidations);
          substep.isInvalid = !validationResult.substepValid;
          substep.isLenientInvalid = !validationResult.substepValid;
        }

        if (substeps[SubstepType.ConfigureCarriage]) {
          const substep = substeps[SubstepType.ConfigureCarriage];
          const validationResult = VALIDATORS[SubstepType.ConfigureCarriage](draft, substep);
          Object.assign(substep.additionalValidations, validationResult.extraValidations);
          substep.isInvalid = !validationResult.substepValid;
          substep.isLenientInvalid = !validationResult.substepValid;
        }

        activateRailsSummaryAfterStep4LengthFilled(draft);

        if (!navigationUpdate && shouldUseNewValidation(draft) && draft.conflicts.length > 0) {
          // Update validation state from conflicts
          const cstics: string[] = getConflictCsticNames(draft.conflicts);

          draft.steps[draft.currentStep].substeps
            .map((substepType) => draft.substeps[substepType])
            .forEach((substep) => validateStepFromConflicts(substep, cstics));
        }

        draft.steps[draft.currentStep].substeps
          .map((substepType) => draft.substeps[substepType])
          .filter((substep) => substep.isInvalid || substep.isLenientInvalid)
          .forEach((substep) => validateStep(draft, substep.type));
      });

      return newState;
    }

    case UPDATE_CSTICS: {
      const { updates, owningSubstep } = action.payload as {
        updates: CsticUpdate[];
        owningSubstep: SubstepType;
      };

      const kmats = constructUpdatedKmats(state.kmats, updates);
      const substeps = { ...state.substeps };

      const newState = {
        ...state,
        addedToCart: false,
        previousState: state.previousState ? state.previousState : { ...state, previousState: undefined },
        kmats,
        substeps,
      };

      if (owningSubstep === SubstepType.GuidewaySet) {
        const extraParams = updateGuidewaySetValidations(newState, true);
        const substep = { ...state.substeps[SubstepType.GuidewaySet] };
        substeps[SubstepType.GuidewaySet] = substep;
        substep.additionalValidations = {
          ...substep.additionalValidations,
          ...extraParams,
        };
        const isInvalid = Object.values(substep.additionalValidations).some((valid) => !valid);
        const isLenientInvalid = Object.entries(substep.additionalValidations).some(([key, valid]) => {
          if (isGuidewayPieceSizeCsticName(key) || key.includes(CSTIC_LENGTH_NAME)) {
            const isDirty = substep.additionalData[fromExtraValidationToDirtyParamName(key)] ?? false;
            return !valid && isDirty;
          }
          return !valid;
        });
        substep.isInvalid = isInvalid;
        substep.isLenientInvalid = isLenientInvalid;
        newState.substeps = substeps;
      }
      if (owningSubstep === SubstepType.ConfigureRail) {
        const extraParams = updateGuidewaySetValidations(newState, true);
        const substep = { ...state.substeps[SubstepType.ConfigureRail] };
        substeps[SubstepType.ConfigureRail] = substep;
        substep.additionalValidations = {
          ...substep.additionalValidations,
          ...extraParams,
        };
        const isInvalid = Object.values(substep.additionalValidations).some((valid) => !valid);

        // const paramAmount =
        const isLenientInvalid = Object.entries(substep.additionalValidations).some(([key, valid]) => {
          if (isGuidewayPieceSizeCsticName(key) || key.includes(CSTIC_LENGTH_NAME)) {
            const isDirty = substep.additionalData[fromExtraValidationToDirtyParamName(key)] ?? false;
            return !valid && isDirty;
          }
          return !valid;
        });
        substep.isInvalid = isInvalid;
        substep.isLenientInvalid = isLenientInvalid;
        newState.substeps = substeps;
      }
      if (owningSubstep === SubstepType.CarriageDetails) {
        const substep = substeps[SubstepType.CarriageDetails];
        const validationResult = VALIDATORS[SubstepType.CarriageDetails](newState, substep);
        substeps[SubstepType.CarriageDetails] = {
          ...substep,
          additionalValidations: {
            ...substep.additionalValidations,
            ...validationResult.extraValidations,
          },
          isInvalid: !validationResult.substepValid,
          isLenientInvalid: !validationResult.substepValid,
        };
      }
      if (owningSubstep === SubstepType.ConfigureCarriage) {
        const substep = substeps[SubstepType.ConfigureCarriage];
        const validationResult = VALIDATORS[SubstepType.CarriageDetails](newState, substep);
        substeps[SubstepType.CarriageDetails] = {
          ...substep,
          additionalValidations: {
            ...substep.additionalValidations,
            ...validationResult.extraValidations,
          },
          isInvalid: !validationResult.substepValid,
          isLenientInvalid: !validationResult.substepValid,
        };
      }

      return newState;
    }

    case UPDATE_ADDITIONAL_DATA: {
      const { updates, owningSubstep } = action.payload as {
        updates: CsticUpdate[];
        owningSubstep: SubstepType;
      };

      return produce(state, (draft) => {
        const substep = draft.substeps[owningSubstep]!;
        updates.forEach((update) => {
          const key = getDirtyParamName(update);
          substep.additionalData[key] = true;
        });
        const isAllDirty = Object.entries(substep.additionalData)
          .filter(([key]) => key.startsWith(EXTRA_DATA_DIRTY_PARAM_PREFIX))
          .every(([_, value]) => value);
        if (isAllDirty) {
          substep.isLenientInvalid = substep.isInvalid;
        }
        draft.addedToCart = false;
      });
    }
    case UPDATE_SMDS_FLIP: {
      const flip = action.payload as boolean;

      return {
        ...state,
        addedToCart: false,
        substeps: {
          ...state.substeps,
          [SubstepType.SMDS]: {
            ...state.substeps[SubstepType.SMDS],
            value: flip,
          },
        },
      };
    }

    case CUSTOM_SEGMENT_LENGTH_SELECTED: {
      const { value, kmatIndex } = action.payload as { value: boolean; kmatIndex: number };

      return {
        ...state,
        addedToCart: false,
        previousState: state.previousState ? state.previousState : { ...state, previousState: undefined },
        customSegmentLengthSelected: kmatIndex === 0 ? value : state.customSegmentLengthSelected,
        customSecondSegmentLengthSelected: kmatIndex === 1 ? value : state.customSecondSegmentLengthSelected,
      };
    }

    case CUSTOM_SEGMENT_LENGTH_SELECTED_RESET: {
      return {
        ...state,
        addedToCart: false,
        previousState: state.previousState ? state.previousState : { ...state, previousState: undefined },
        customSegmentLengthSelected: false,
        customSecondSegmentLengthSelected: false,
      };
    }

    case UPDATE_CSTICS_FROM_TILE: {
      const { elementType, tileIndex, tile } = action.payload as {
        elementType: SubstepType;
        tileIndex: number;
        tile: SubstepTile;
      };

      const substeps: Dict<LinearSubstep> = {
        ...state.substeps,
        [elementType]: {
          ...state.substeps[elementType],
          selectedTile: tileIndex,
          isInvalid: false,
          isLenientInvalid: false,
        },
      };
      const kmats = constructUpdatedKmats(state.kmats, csticUpdatesFromDict(tile.cstics));

      return {
        ...state,
        addedToCart: false,
        previousState: state.previousState ? state.previousState : { ...state, previousState: undefined },
        kmats,
        substeps,
      };
    }

    case RESET_CURRENT_STEP: {
      const stepData = state.steps[state.currentStep];

      const kmats = constructUpdatedKmats(state.kmats, csticUpdatesFromDict(stepData.defaultValues));

      const substeps = { ...state.substeps };
      stepData.substeps.forEach((elementType) => {
        substeps[elementType] = {
          ...substeps[elementType],
          selectedTile: findSelectedTileIndex(substeps[elementType], kmats),
          isInvalid: false,
          isLenientInvalid: false,
        };
      });

      let newState: LinearConfiguratorState = {
        ...state,
        previousState: undefined,
        addedToCart: false,
        kmats,
        substeps,
      };

      stepData.substeps.forEach((elementType) => {
        const specialBehavior = SPECIAL_RESET_BEHAVIOR[elementType];
        if (specialBehavior) {
          newState = specialBehavior(newState);
        }
      });

      return newState;
    }

    case VALIDATE_CURRENT_STEP: {
      return produce(state, (draft) => {
        draft.steps[state.currentStep].substeps.forEach((substep) => validateStep(draft, substep));
      });
    }

    case SET_STEP: {
      const index = action.payload as number;

      const highTideStep = index > state.highTideStep ? index : state.highTideStep;

      return {
        ...state,
        currentStep: index,
        highTideStep,
      };
    }

    case SET_TAB: {
      const a = action as ReturnType<typeof setLCSummaryTabAction>;
      const tab = a.payload;

      if (tab === state.activeTab) {
        return state;
      }

      return {
        ...state,
        activeTab: tab,
      };
    }

    case RESTORE_SNAPSHOT: {
      return state.previousState ? { ...state.previousState } : state;
    }

    case UPDATE_QUANTITY: {
      const newQ = action.payload as number;
      return {
        ...state,
        quantity: newQ,
      };
    }

    case UPDATE_PRICE_REFERENCE: {
      const newPR = action.payload as string;
      return {
        ...state,
        priceReference: newPR,
        addedToCart: false,
      };
    }

    case MARK_ALREADY_ADDED_TO_CART: {
      const newConfigId = action.payload;
      if (newConfigId) {
        return {
          ...state,
          configId: newConfigId,
        };
      } else {
        return {
          ...state,
          addedToCart: true,
        };
      }
    }

    case TOGGLE_LINEAR_CARRIAGE_EDIT: {
      const { carriageIndex, statusEdit } = action.payload as {
        carriageIndex: number;
        statusEdit: boolean;
      };

      const temp = state.enableCarriageEdit?.map((item, index) => {
        if (carriageIndex === undefined || index === carriageIndex) {
          return statusEdit;
        }

        return item;
      });

      return {
        ...state,
        addedToCart: false,
        enableCarriageEdit: temp,
      };
    }

    default: {
      return state;
    }
  }
};

type ValidatorFunc = (
  state: LinearConfiguratorState,
  substep: LinearSubstep,
) => {
  substepValid: boolean;
  extraValidations: Dict<boolean>;
};

const DEFAULT_VALIDATION: ValidatorFunc = (state, substep) => ({
  substepValid: substep.selectedTile !== null || substep.value !== null,
  extraValidations: {},
});

const VALIDATORS: Dict<ValidatorFunc> = {
  [SubstepType.LocatingFace]: () => ({
    // There are always some values selected in Locating Face
    substepValid: true,
    extraValidations: {},
  }),
  [SubstepType.CarriageDetails]: (state, substep) => {
    const validations = updateCarriageValidations(state, false);
    return {
      substepValid: Object.values(validations).every((valid) => valid),
      extraValidations: validations,
    };
  },
  [SubstepType.ConfigureCarriage]: (state, substep) => {
    const validations = updateCarriageValidations(state, false);
    return {
      substepValid: Object.values(validations).every((valid) => valid),
      extraValidations: validations,
    };
  },
  [SubstepType.SummaryPage]: () => ({
    substepValid: true,
    extraValidations: {},
  }),
  [SubstepType.Length]: (state, substep) => {
    const mainKmat = getMainKmat({ kmats: state.kmats });
    const lengthCstic = mainKmat.cstics[FAKE_CSTIC_UNIT_LENGTH];
    const length = lengthCstic.value ? lengthCstic.value : "0";
    const isValid = isLengthValid(state.minLength, state.maxLength, Number.parseFloat(length));
    return {
      substepValid: isValid,
      extraValidations: {
        [getExtraValidationParamName(SimpleCsticId(FAKE_CSTIC_UNIT_LENGTH))]: isValid,
      },
    };
  },
  [SubstepType.ShaftLength]: (state, substep) => {
    const configuratorType = state.type!;
    let csticName, kmatType;
    if (configuratorType === ConfiguratorType.MetricShaft) {
      csticName = MetricCstics.SHAFT_LENGTH;
      kmatType = KmatType.METRIC_SHAFT;
    } else {
      csticName = ImperialCstics.SHAFT_LENGTH_METRIC;
      kmatType = KmatType.INCH_SHAFT;
    }

    const lengthCstic = selectCsticExpandedFromNested(
      { key: csticName, index: 0, type: kmatType },
      substep.type,
    )(state);
    const invalid = (lengthCstic.isDirty && lengthCstic.isInConflict) || !lengthCstic.cstic.value;
    return {
      substepValid: !invalid,
      extraValidations: {},
    };
  },
  [SubstepType.ShaftMachining]: (state, substep) => {
    const configuratorType = state.type!;
    let csticNames: string[];
    if (configuratorType === ConfiguratorType.MetricShaft) {
      csticNames = [
        MetricCstics.SHAFT_MACHINING_LEFT,
        MetricCstics.SHAFT_MACHINING_MIDDLE,
        MetricCstics.SHAFT_MACHINING_RIGHT,
      ];
    } else {
      csticNames = [
        ImperialCstics.SHAFT_MACHINING_LEFT,
        ImperialCstics.SHAFT_MACHINING_MIDDLE,
        ImperialCstics.SHAFT_MACHINING_RIGHT,
      ];
    }

    const isInvalid = csticNames.some((csticName) => {
      const cstic = selectCsticExpandedFromNested(SimpleCsticId(csticName), substep.type)(state);
      // This is a selectable cstic, not input, so we don't need to check isDirty
      // TODO - Properly update isDirty for selectable cstics
      // return cstic.isDirty && cstic.isInConflict;
      return cstic.isInConflict || !cstic.cstic.value;
    });

    const extraValidations = {};
    let isSubstepValid = !isInvalid;

    if (configuratorType === ConfiguratorType.InchShaft) {
      const conflictedCstics = state.conflicts.flatMap((conflict) => conflict.cstics).map((cstic) => cstic.name);
      const kmat = getMainKmat({ kmats: state.kmats });

      const validationCtx = { kmat, conflictedCstics, mode: "full" } as const;
      const addValidationParams = (errors: ShaftMachiningExtraError[]) => {
        errors.forEach((error) => {
          const paramName = getExtraValidationParamName(SimpleCsticId(error.cstic));
          extraValidations[paramName] = false;
        });
      };

      const leftMachining = kmat.cstics[ImperialCstics.SHAFT_MACHINING_LEFT]?.value;
      if (showChamferedSection(leftMachining)) {
        const errors = validateChamferSegment(ImperialCstics.chamfer.left, validationCtx);
        isSubstepValid &&= errors.length === 0;
        addValidationParams(errors);
      }

      const rightMachining = kmat.cstics[ImperialCstics.SHAFT_MACHINING_RIGHT]?.value;
      if (showChamferedSection(rightMachining)) {
        const errors = validateChamferSegment(ImperialCstics.chamfer.right, validationCtx);
        isSubstepValid &&= errors.length === 0;
        addValidationParams(errors);
      }
    }

    return {
      substepValid: isSubstepValid,
      extraValidations,
    };
  },
  [SubstepType.GuidewaySet]: (state, substep) => {
    const validations = updateGuidewaySetValidations(state, false);
    const validationsOk = Object.values(validations).every((valid) => valid);

    const hasLengthSuggestions = state.kmats[KmatType.GUIDEWAY].some(
      (kmat) =>
        Object.entries(kmat.cstics).filter(
          ([csticName, _]) => csticName.endsWith("_U_64") || csticName.endsWith("_O_64"),
        ).length > 0,
    );

    return {
      substepValid: validationsOk && !hasLengthSuggestions,
      extraValidations: validations,
    };
  },
  [SubstepType.ConfigureRail]: (state, substep) => {
    if (state.productCode === TKDM_RAIL_CONFIGURATOR_PRODUCT_CODE) {
      return {
        substepValid: true,
        extraValidations: {},
      };
    }
    const validations = updateGuidewaySetValidations(state, false);
    const validationsOk = Object.values(validations).every((valid) => valid);

    const hasLengthSuggestions = state.kmats[KmatType.GUIDEWAY].some(
      (kmat) =>
        Object.entries(kmat.cstics).filter(
          ([csticName, _]) => csticName.endsWith("_U_64") || csticName.endsWith("_O_64"),
        ).length > 0,
    );

    return {
      substepValid: validationsOk && !hasLengthSuggestions,
      extraValidations: validations,
    };
  },
  [SubstepType.ReferenceCarriage]: () => ({
    // Reference is always true, because it sometimes may have no value
    substepValid: true,
    extraValidations: {},
  }),
  [SubstepType.SMDS]: (state, substep) => ({
    // Valid if not visible
    substepValid: substep?.isVisible ? DEFAULT_VALIDATION(state, substep).substepValid : true,
    extraValidations: {},
  }),
  [SubstepType.SMDE]: (state, substep) => ({
    // Valid if not visible
    substepValid: substep?.isVisible ? DEFAULT_VALIDATION(state, substep).substepValid : true,
    extraValidations: {},
  }),
  [SubstepType.Closure]: (state, substep) => {
    if (state.productCode === TKDM_RAIL_CONFIGURATOR_PRODUCT_CODE) {
      return {
        substepValid: true,
        extraValidations: {},
      };
    }
    // If last tile in first group is selected then we have an error, because
    // after selecting that tile we should load a new group and automatically change selection
    if (isFakeNoClosingPLugsTileSelected(substep)) {
      return {
        substepValid: false,
        extraValidations: {},
      };
    }
    return DEFAULT_VALIDATION(state, substep);
  },

  [SubstepType.ShaftMachiningExtra]: (state, substep) => {
    const kmat = getMainKmat({ kmats: state.kmats });

    const leftMachining = kmat.cstics[MetricCstics.SHAFT_MACHINING_LEFT]?.value;
    const middleMachining = kmat.cstics[MetricCstics.SHAFT_MACHINING_MIDDLE]?.value;
    const rightMachining = kmat.cstics[MetricCstics.SHAFT_MACHINING_RIGHT]?.value;

    const conflictedCstics = state.conflicts.flatMap((conflict) => conflict.cstics).map((cstic) => cstic.name);

    const validationCtx = { kmat, conflictedCstics, mode: "full" } as const;

    let isSubstepValid = true;
    const extraValidations = {};
    const addValidationParams = (errors: ShaftMachiningExtraError[]) => {
      errors.forEach((error) => {
        const paramName = getExtraValidationParamName(SimpleCsticId(error.cstic));
        extraValidations[paramName] = false;
      });
    };

    if (showChamferedSection(leftMachining)) {
      const errors = validateChamferSegment(MetricCstics.chamfer.left, validationCtx);
      isSubstepValid &&= errors.length === 0;
      addValidationParams(errors);
    }
    if (showChamferedSection(rightMachining)) {
      const errors = validateChamferSegment(MetricCstics.chamfer.right, validationCtx);
      isSubstepValid &&= errors.length === 0;
      addValidationParams(errors);
    }
    if (showRadialThreadSection(middleMachining)) {
      const errors = validateRadialThreadMetric(validationCtx);
      isSubstepValid &&= errors.length === 0;
      addValidationParams(errors);
    }
    if (showAxialThreadSection(leftMachining)) {
      const errors = validateAxialThread(axialThread.left, validationCtx);
      isSubstepValid &&= errors.length === 0;
      addValidationParams(errors);
    }
    if (showAxialThreadSection(rightMachining)) {
      const errors = validateAxialThread(axialThread.right, validationCtx);
      isSubstepValid &&= errors.length === 0;
      addValidationParams(errors);
    }

    return {
      substepValid: isSubstepValid,
      extraValidations,
    };
  },
};

const SPECIAL_RESET_BEHAVIOR = {
  [SubstepType.Length]: (state: LinearConfiguratorState) => {
    const mainKmat = getMainKmat({ kmats: state.kmats });
    const lengthCstic = mainKmat.cstics[FAKE_CSTIC_UNIT_LENGTH];
    if (lengthCstic) {
      lengthCstic.value = undefined;
    }

    return state;
  },
};

const updateGuidewaySetValidations = (state: LinearConfiguratorState, ignoreEmpty: boolean) => {
  const extraParameters: Dict<boolean> = {};
  state.kmats[KmatType.GUIDEWAY]?.forEach((kmat, kmatIndex) => {
    // Validate Length
    let validationObj = {
      index: kmatIndex,
      type: KmatType.GUIDEWAY,
      key: CSTIC_LENGTH_NAME,
      value: getCsticVal(kmat.cstics[CSTIC_LENGTH_NAME]),
    };
    let valid = GUIDEWAY_LENGTH_VALIDATOR(state, validationObj, ignoreEmpty);
    const extraParamName = getExtraValidationParamName(validationObj);
    extraParameters[extraParamName] = valid;

    // Validate all partial lengths
    let index = 1;

    while (true) {
      const csticKey = GET_GUIDEWAY_PIECE_SIZE_CSTIC_NAME(index);

      const cstic = kmat.cstics[csticKey];
      if (!cstic) {
        break;
      }

      validationObj = {
        index: kmatIndex,
        type: KmatType.GUIDEWAY,
        key: csticKey,
        value: getCsticVal(kmat.cstics[csticKey]),
      };

      valid = GUIDEWAY_PART_LENGTH_VALIDATOR(state, validationObj, false);
      const extraParamName = getExtraValidationParamName({
        index: kmatIndex,
        type: KmatType.GUIDEWAY,
        key: csticKey,
      });
      extraParameters[extraParamName] = valid;

      index++;
    }
    // Validate ALAR
    for (const csticKey of [CSTIC_AL_NAME, CSTIC_AR_NAME]) {
      const validationObj = {
        index: kmatIndex,
        type: KmatType.GUIDEWAY,
        key: csticKey,
        value: getCsticVal(kmat.cstics[csticKey]),
      };
      const valid = GUIDEWAY_EXTENDED_ALAR_VALIDATOR(state, validationObj, ignoreEmpty);
      const extraParamName = getExtraValidationParamName(validationObj);
      extraParameters[extraParamName] = valid;
    }
  });
  return extraParameters;
};

const updateCarriageValidations = (state: LinearConfiguratorState, ignoreEmpty = false) => {
  const extraParameters: Dict<boolean> = {};
  state.kmats[KmatType.CARRIAGE].forEach((kmat, kmatIndex) => {
    getEditCarriageCsticsToReset(state.type!).forEach((csticKey) => {
      if (kmat.cstics[csticKey] !== null && kmat.cstics[csticKey] !== undefined) {
        const validationObj = {
          index: kmatIndex,
          type: KmatType.CARRIAGE,
          key: csticKey,
          value: getCsticVal(kmat.cstics[csticKey]),
        };
        const position = getCsticVal(kmat.cstics[CSTIC_SCHMIERANSCHLUSS_NAME]);
        let valid;
        if (
          csticKey === CSTIC_SCHMIERANSCHLUSS_POSITION_NAME &&
          (position === UNMOUNTED_LEFT || position === UNMOUNTED_RIGHT)
        ) {
          // Don't validate position if it's already mounted
          valid = true;
        } else {
          valid = EDIT_CARRIAGE_VALUE_VALIDATOR(state, validationObj, false);
        }
        const extraParamName = getExtraValidationParamName(validationObj);
        extraParameters[extraParamName] = valid;
      }
    });
  });
  return extraParameters;
};

type CsticValidator = (state: LinearConfiguratorState, update: CsticUpdate, ignoreEmpty: boolean) => boolean;

const GUIDEWAY_EXTENDED_ALAR_VALIDATOR: CsticValidator = (state, update, ignoreEmpty) => {
  if (!update.value) {
    return ignoreEmpty;
  }
  let valid = true;
  const value = +update.value;

  const minSize = 0;
  valid = valid && value > minSize;

  const maxSize = getCsticNumberVal(state.kmats[KmatType.GUIDEWAY][update.index!].cstics[AlarHardMax]);
  if (maxSize !== null) {
    valid = valid && value <= maxSize;
  }
  return valid;
};

const GUIDEWAY_LENGTH_VALIDATOR: CsticValidator = (state, update, ignoreEmpty) => {
  if (!update.value) {
    return ignoreEmpty;
  }
  let valid = true;
  if (state.minLength && +update.value < state.minLength) {
    valid = false;
  }
  if (state.maxLength && +update.value > state.maxLength) {
    valid = false;
  }
  return valid;
};

const GUIDEWAY_PART_LENGTH_VALIDATOR: CsticValidator = (state, update, ignoreEmpty) => {
  if (!update.value) {
    return ignoreEmpty;
  }
  const cstics = state.kmats[KmatType.GUIDEWAY][update.index!].cstics;

  let valid = true;

  const minPartLength = getCsticNumberVal(cstics[CSTIC_PART_MIN_SIZE_NAME]);
  if (minPartLength && +update.value < minPartLength) {
    valid = false;
  }

  const maxPartLength = getCsticNumberVal(cstics[CSTIC_PART_MAX_SIZE_NAME]);
  if (maxPartLength && +update.value > maxPartLength) {
    valid = false;
  }
  return valid;
};

const EDIT_CARRIAGE_VALUE_VALIDATOR: CsticValidator = (state, update, ignoreEmpty) => {
  return update && update.value != null;
};

function constructUpdatedKmats(kmats: Dict<Kmat[]>, dataToUpdate: CsticUpdate[]) {
  const updatedKmats = JSON.parse(JSON.stringify(kmats)) as Dict<Kmat[]>;
  dataToUpdate.forEach(({ key, type, index, value }) => {
    const kmatList = updatedKmats[type];
    if (kmatList) {
      kmatList.forEach((kmat, kmatIndex) => {
        if (index === undefined || index === kmatIndex) {
          const cstic = kmat.cstics[key];
          if (cstic) {
            kmat.cstics[key] = {
              ...cstic,
              value: value === null ? undefined : value,
            };
          }
        }
      });
    }
  });
  return updatedKmats;
}

export function isLengthValid(minLength: number | null, maxLength: number | null, length: number) {
  let valid = true;
  if (minLength !== null) {
    valid = valid && length >= minLength;
  }
  if (maxLength !== null) {
    valid = valid && length <= maxLength;
  }
  return valid;
}

function activateRailsSummaryAfterStep4LengthFilled(state: LinearConfiguratorState) {
  if (state.type !== ConfiguratorType.Rail || state.productCode !== RAIL_CONFIGURATOR_PRODUCT_CODE) {
    return;
  }
  // hacky solution for inconsistent with other configurators requirement https://agile.deloitte.de/browse/SCHAEFF-18157
  // other configurators do not allow to enable stepper step in the middle of other step
  const validationObj = {
    index: 1,
    type: KmatType.GUIDEWAY,
    key: CSTIC_LENGTH_NAME,
    value: getCsticVal(state.kmats[KmatType.GUIDEWAY][0].cstics[CSTIC_LENGTH_NAME]),
  };
  const isLengthValid = GUIDEWAY_LENGTH_VALIDATOR(state, validationObj, false);
  const isStep4Active = state.steps[3].active;
  if (isStep4Active && isLengthValid) {
    state.steps[4].active = true;
  }
}
function getConflictCsticNames(conflicts: ConflictKmat[]): string[] {
  return conflicts.flatMap((conflict) => conflict.cstics.map((cstic) => cstic.name));
}
function validateStepFromConflicts(substep: LinearSubstep, conflictedCstics: string[]): void {
  if (substep.inputCstics) {
    Object.values(substep.inputCstics)
      .filter((cstic) => cstic.required && cstic.visible)
      .forEach((cstic) => {
        if (conflictedCstics.includes(cstic.name)) {
          substep.isInvalid = true;
          substep.isLenientInvalid = true;
          const key = getExtraValidationParamName(SimpleCsticId(cstic.name));
          substep.additionalValidations[key] = false;
        }
      });
  }
}
